import { Component, Input, OnDestroy, OnInit, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { select } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/core/services/language.service';
import { UiState } from 'src/app/core/state/ui.state';
import { datepickerValueToDate } from '../../functions/datepicker-value-to-date';

export interface UiDateRangeValue {
  from: Date;
  to: Date;
}

@Component({
  selector: 'app-ui-date-range-picker',
  templateUrl: './ui-date-range-picker.component.html',
  styleUrls: ['./ui-date-range-picker.component.scss'],
  standalone: false,
})
export class UiDateRangePickerComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);

  @Input() control: FormControl<UiDateRangeValue | null>;
  @Input() maxDate: Date | null = null;
  @Input() tooltipKey = 'customRange';
  @Input() removeTooltipKey = 'removeCustomRange';
  @Input() fromLabelKey = 'fromDate';
  @Input() toLabelKey = 'toDate';
  @Input() cancelKey = 'cancel';
  @Input() applyKey = 'apply';

  fromControl = new FormControl<Date | null>(null);
  toControl = new FormControl<Date | null>(null);
  hasCustomRange = false;
  customRangeDisplayText = '';

  private menuTrigger?: MatMenuTrigger;
  private currentLocale: string;
  private subscription = new Subscription();

  private readonly uiLanguage = select(UiState.language);

  constructor(private languageService: LanguageService) {
    const currentLang = this.languageService.getCurrentLanguage();
    this.currentLocale = this.languageService.getLocaleForLanguage(currentLang);
  }

  ngOnInit(): void {
    this.syncFromControl(this.control?.value ?? null);
    this.subscription.add(
      this.control.valueChanges.subscribe((range) => {
        this.syncFromControl(range);
      })
    );
    this.subscription.add(
      toObservable(this.uiLanguage, { injector: this.injector }).subscribe((language: string) => {
        this.currentLocale = this.languageService.getLocaleForLanguage(language);
        if (this.hasCustomRange && this.control?.value) {
          this.customRangeDisplayText = this.formatRangeLabel(this.control.value);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onMenuOpened(trigger: MatMenuTrigger): void {
    this.menuTrigger = trigger;
    this.syncDraftControls(this.control?.value ?? null);
  }

  applyCustomRange(): void {
    if (!this.isCustomRangeValid()) {
      return;
    }

    const from = this.fromControl.value;
    const to = this.toControl.value;
    if (!from || !to) {
      return;
    }

    const range: UiDateRangeValue = {
      from: datepickerValueToDate(from),
      to: datepickerValueToDate(to),
    };
    this.control.setValue(range);
    this.closeMenu();
  }

  cancelCustomRange(): void {
    this.syncDraftControls(this.control?.value ?? null);
    this.closeMenu();
  }

  clearCustomRange(): void {
    this.control.setValue(null);
    this.closeMenu();
  }

  isCustomRangeValid(): boolean {
    const from = this.fromControl.value;
    const to = this.toControl.value;
    if (!from || !to) {
      return false;
    }
    const fromDate = datepickerValueToDate(from);
    const toDate = datepickerValueToDate(to);
    return fromDate <= toDate;
  }

  getFromMaxDate(): Date | null {
    const toValue = this.toControl.value;
    const toDate = toValue ? datepickerValueToDate(toValue) : null;

    if (!toDate && !this.maxDate) {
      return null;
    }

    if (!toDate) {
      return this.maxDate;
    }

    if (!this.maxDate) {
      return toDate;
    }

    return toDate < this.maxDate ? toDate : this.maxDate;
  }

  getToMinDate(): Date | null {
    const fromValue = this.fromControl.value;
    return fromValue ? datepickerValueToDate(fromValue) : null;
  }

  private syncFromControl(range: UiDateRangeValue | null): void {
    if (!range) {
      this.hasCustomRange = false;
      this.customRangeDisplayText = '';
      return;
    }

    this.hasCustomRange = true;
    this.customRangeDisplayText = this.formatRangeLabel(range);
  }

  private syncDraftControls(range: UiDateRangeValue | null): void {
    if (!range) {
      this.fromControl.setValue(null, { emitEvent: false });
      this.toControl.setValue(null, { emitEvent: false });
      return;
    }

    this.fromControl.setValue(range.from, { emitEvent: false });
    this.toControl.setValue(range.to, { emitEvent: false });
  }

  private formatRangeLabel(range: UiDateRangeValue): string {
    const fromFormatted = this.formatDateShort(range.from);
    const toFormatted = this.formatDateShort(range.to);
    return `${fromFormatted} – ${toFormatted}`;
  }

  private formatDateShort(date: Date): string {
    return new Intl.DateTimeFormat(this.currentLocale, {
      month: 'short',
      day: '2-digit',
    }).format(date);
  }

  private closeMenu(): void {
    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
  }
}
